<div class="main-content">
	<div class="container-fluid">
		<div class="col-md-12">
			<div class="card">
				<div class="card-header card-header-{{color}}-6 d-flex justify-content-between align-items-center">
					<h4 class="card-title mb-0" (click)="back()" style="cursor:pointer;">
						<i class="material-icons">arrow_left</i>
						Pending payment runs
					</h4>
					<button type="button" class="btn btn-sm btn-light" (click)="newRun()">
						<i class="fa fa-plus"></i> New payment run
					</button>
				</div>
				<div class="card-body">

					<!-- Run-context summary strip — same visual language as the payment run screen. -->
					<div class="ppr-context" *ngIf="!loading">
						<div class="ppr-context__field">
							<span class="ppr-context__label">Pending</span>
							<span class="ppr-context__value">{{ runs.length }}</span>
						</div>
						<div class="ppr-context__field" *ngIf="summaryTotal">
							<span class="ppr-context__label">Combined total</span>
							<span class="ppr-context__value">{{ formatMoney(summaryTotal) }}</span>
						</div>
						<div class="ppr-context__field" *ngIf="summaryLines">
							<span class="ppr-context__label">Invoice lines</span>
							<span class="ppr-context__value">{{ summaryLines }}</span>
						</div>
						<div class="ppr-context__hint">
							Runs saved but not posted. Open one to review &amp; post — or discard to remove.
						</div>
					</div>

					<!-- Filter + sort toolbar. -->
					<div class="ppr-toolbar" *ngIf="!loading && runs.length > 0">
						<div class="ppr-toolbar__search">
							<i class="fa fa-search ppr-toolbar__search-icon"></i>
							<input
								type="text"
								class="ppr-toolbar__search-input"
								placeholder="Search by journal #, vendor name, or user…"
								[(ngModel)]="filterText"
							/>
							<button *ngIf="filterText" class="ppr-toolbar__clear" type="button" (click)="clearFilter()" aria-label="Clear search">
								<i class="fa fa-times"></i>
							</button>
						</div>
						<div class="ppr-toolbar__sort">
							<label class="ppr-toolbar__sort-label">Sort</label>
							<select class="ppr-toolbar__sort-select" [(ngModel)]="sortMode">
								<option value="newest">Newest first</option>
								<option value="oldest">Oldest first</option>
								<option value="largest">Largest amount</option>
								<option value="smallest">Smallest amount</option>
							</select>
						</div>
					</div>

					<!-- Loading skeleton — three placeholder cards while data fetches. -->
					<div *ngIf="loading" class="runs-list">
						<div class="ppr-skeleton" *ngFor="let _ of [1,2,3]">
							<div class="ppr-skeleton__bar ppr-skeleton__bar--lg"></div>
							<div class="ppr-skeleton__bar ppr-skeleton__bar--md"></div>
							<div class="ppr-skeleton__bar ppr-skeleton__bar--sm"></div>
						</div>
					</div>

					<!-- Genuinely empty (no pending runs at all). -->
					<div *ngIf="!loading && runs.length === 0" class="ppr-empty">
						<i class="fa fa-inbox ppr-empty__icon"></i>
						<p class="ppr-empty__title">No pending payment runs.</p>
						<p class="ppr-empty__hint">Saved-for-review runs land here. Create one to get started.</p>
						<button type="button" class="btn btn-primary mt-2" (click)="newRun()">
							<i class="fa fa-plus"></i> Create payment run
						</button>
					</div>

					<!-- Filter wiped the list. -->
					<div *ngIf="!loading && runs.length > 0 && filteredRuns.length === 0" class="ppr-empty ppr-empty--filter">
						<i class="fa fa-filter ppr-empty__icon"></i>
						<p class="ppr-empty__title">No runs match "{{ filterText }}".</p>
						<button type="button" class="btn btn-outline-secondary btn-sm mt-2" (click)="clearFilter()">Clear search</button>
					</div>

					<div *ngIf="!loading && filteredRuns.length > 0" class="ppr-grid">
						<div
							*ngFor="let run of filteredRuns; trackBy: trackByJournal"
							class="ppr-card"
							[class.ppr-card--stale]="isStale(run)"
							(click)="openRun(run)"
							role="button"
							tabindex="0"
							(keydown.enter)="openRun(run)"
							(keydown.space)="openRun(run); $event.preventDefault()"
						>
							<div class="ppr-card__topbar">
								<div class="ppr-card__journal">
									<span class="ppr-card__journal-label">Run</span>
									<span class="ppr-card__journal-value">#{{ run.journalno }}</span>
								</div>
								<span class="ppr-card__stale-pill" *ngIf="isStale(run)" title="Pending more than 3 days — consider posting or discarding.">
									<i class="fa fa-clock-o"></i> Stale
								</span>
							</div>

							<div class="ppr-card__amount-row">
								<div class="ppr-card__amount">{{ formatMoney(run.totalAmount, run.currency || 'USD') }}</div>
								<div class="ppr-card__counts">
									<div><b>{{ run.vendorCount }}</b> {{ run.vendorCount === 1 ? 'vendor' : 'vendors' }}</div>
									<div *ngIf="run.lineCount"><b>{{ run.lineCount }}</b> {{ run.lineCount === 1 ? 'line' : 'lines' }}</div>
								</div>
							</div>

							<div class="ppr-card__vendors" *ngIf="run.vendorNames?.length">
								<i class="fa fa-building-o ppr-card__vendors-icon"></i>
								<span>{{ run.vendorNames.slice(0, 4).join(' · ') }}<span *ngIf="run.vendorCount > 4"> · +{{ run.vendorCount - 4 }} more</span></span>
							</div>

							<div class="ppr-card__footer">
								<div class="ppr-card__author">
									<span class="ppr-card__avatar" *ngIf="run.created_by">{{ initialsOf(run.created_by) }}</span>
									<span class="ppr-card__author-text">
										<span *ngIf="run.created_by" class="ppr-card__author-name">{{ run.created_by }}</span>
										<span class="ppr-card__author-time" [title]="formatDate(run.created_at || run.dateran)">
											{{ relativeTime(run.created_at || run.dateran) }}
										</span>
									</span>
								</div>
								<div class="ppr-card__actions">
									<button
										type="button"
										class="btn btn-sm btn-primary"
										(click)="openRun(run); $event.stopPropagation()"
									>
										Open <i class="fa fa-arrow-right"></i>
									</button>
									<button
										type="button"
										class="ppr-card__discard"
										(click)="discardRun(run, $event)"
										[disabled]="discardingJournal === run.journalno"
										[title]="discardingJournal === run.journalno ? 'Discarding…' : 'Discard this run (nothing posted)'"
									>
										<span *ngIf="discardingJournal === run.journalno" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
										<i *ngIf="discardingJournal !== run.journalno" class="fa fa-trash"></i>
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
